// @ts-nocheck
// Migrated from the legacy browser rules. The module is intentionally DOM-free.
import type { CharacterCatalog, CharacterDraft } from "./types";

const SPECIAL_MIN = 4;
const SPECIAL_MAX = 10;
const MAX_LEVEL = 21;
const MAX_SKILL_RANK = 6;
const MAIN_SKILL_LIMIT = 3;
const CREATION_SKILL_CAP = 3;

const clone = (value) => {
  if (value === undefined) return value;
  return JSON.parse(JSON.stringify(value));
};

const numberOr = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

function normalizeCatalog(catalog = {}) {
  return {
    origins: Array.isArray(catalog.origins) ? catalog.origins : [],
    special: Array.isArray(catalog.special) ? catalog.special : [],
    skills: Array.isArray(catalog.skills) ? catalog.skills : [],
    equipment: Array.isArray(catalog.equipment) ? catalog.equipment : [],
    equipment_sets:
      catalog.equipment_sets && typeof catalog.equipment_sets === "object"
        ? catalog.equipment_sets
        : {},
    perks: Array.isArray(catalog.perks) ? catalog.perks : [],
  };
}

function defaultSkills(catalog) {
  return Object.fromEntries(
    catalog.skills.map((skill) => [
      skill.name,
      {
        base: 0,
        invested: 0,
        is_main: false,
      },
    ]),
  );
}

function defaultDraft(catalog) {
  const baseSpecial = Object.fromEntries(
    catalog.special.map((attribute) => [attribute.key, 5]),
  );
  return {
    level: 1,
    name: "",
    gender: "",
    backstory: "",
    portrait: "",
    portrait_original: "",
    origin: null,
    origin_traits: [],
    is_robot: false,
    special: clone(baseSpecial),
    special_bonus: Object.fromEntries(
      Object.keys(baseSpecial).map((key) => [key, 0]),
    ),
    special_max: Object.fromEntries(
      Object.keys(baseSpecial).map((key) => [key, SPECIAL_MAX]),
    ),
    bonus_special_points: 0,
    bonus_special_applied: false,
    protected_special_attrs: [],
    skills: defaultSkills(catalog),
    locked_main_skills: [],
    locked_skill_ranks: {},
    skill_max_rank: MAX_SKILL_RANK,
    perks: {},
    bonus_perk_slots: 0,
    equipment: [],
    resistances: {
      poison: 0,
      radiation: 0,
      physical: 0,
      energy: 0,
      disease: 0,
    },
    carry_weight_modifier: 0,
    carry_weight_formula: null,
    combat_modifiers: {
      melee_damage_bonus: 0,
      second_action_cost: null,
      complication_range: [],
    },
    luck_points: 5,
    luck_points_max: 5,
    pending_choices: [],
    secondary_stats: {},
  };
}

export class CharacterRules {
  constructor(catalog = {}) {
    this.catalog = normalizeCatalog(catalog);
    this.skillNames = new Set(this.catalog.skills.map((skill) => skill.name));
    this.equipmentById = new Map(
      this.catalog.equipment.map((item) => [item.id, item]),
    );
    this.baseSpecial = Object.fromEntries(
      this.catalog.special.map((attribute) => [attribute.key, 5]),
    );
    this.specialKeys = new Set(Object.keys(this.baseSpecial));
  }

  createDraft() {
    const draft = defaultDraft(this.catalog);
    this.recalculate(draft);
    return draft;
  }

  hydrateDraft(data) {
    const fresh = this.createDraft();
    const source = data && typeof data === "object" ? data : {};
    const draft = { ...fresh, ...clone(source) };
    draft.level = Math.min(MAX_LEVEL, Math.max(1, numberOr(draft.level, 1)));
    draft.origin_traits = Array.isArray(draft.origin_traits)
      ? draft.origin_traits.slice(0, 2)
      : [];
    draft.special = { ...fresh.special, ...(draft.special || {}) };
    draft.special_bonus = {
      ...fresh.special_bonus,
      ...(draft.special_bonus || {}),
    };
    draft.special_max = { ...fresh.special_max, ...(draft.special_max || {}) };
    draft.skills = { ...fresh.skills, ...(draft.skills || {}) };
    for (const name of this.skillNames) {
      draft.skills[name] = {
        base: numberOr(draft.skills[name]?.base, 0),
        invested: Math.max(0, numberOr(draft.skills[name]?.invested, 0)),
        is_main: Boolean(draft.skills[name]?.is_main),
      };
    }
    draft.perks = { ...(draft.perks || {}) };
    draft.locked_main_skills = Array.isArray(draft.locked_main_skills)
      ? draft.locked_main_skills
      : [];
    draft.locked_skill_ranks = { ...(draft.locked_skill_ranks || {}) };
    draft.protected_special_attrs = Array.isArray(draft.protected_special_attrs)
      ? draft.protected_special_attrs
      : [];
    draft.pending_choices = Array.isArray(draft.pending_choices)
      ? draft.pending_choices
      : [];
    draft.resistances = { ...fresh.resistances, ...(draft.resistances || {}) };
    draft.combat_modifiers = {
      ...fresh.combat_modifiers,
      ...(draft.combat_modifiers || {}),
    };
    draft.is_robot = Boolean(draft.is_robot);
    this.recalculate(draft);
    return draft;
  }

  setLevel(draft, level) {
    draft.level = Math.min(MAX_LEVEL, Math.max(1, numberOr(level, 1)));
    this.validateSkills(draft);
    this.recalculate(draft);
    return true;
  }

  setOrigin(draft, originName) {
    const origin = this.findOrigin(originName);
    if (!origin) return false;
    draft.origin = origin.name;
    draft.origin_traits = [];
    draft.is_robot = Boolean(origin.is_robot);
    this.resetTraitState(draft);
    this.recalculate(draft);
    return true;
  }

  toggleOriginTrait(draft, traitName) {
    const origin = this.findOrigin(draft.origin);
    const trait = origin?.traits?.find((item) => item.name === traitName);
    if (!origin || !trait) return false;

    const selected = draft.origin_traits.includes(traitName)
      ? draft.origin_traits.filter((name) => name !== traitName)
      : [...draft.origin_traits, traitName];
    if (selected.length > 2 || !this.validTraitSelection(origin, selected))
      return false;

    draft.origin_traits = selected;
    this.rebuildTraitState(draft, origin, selected);
    return true;
  }

  setSpecial(draft, values) {
    if (!values || typeof values !== "object") return false;
    const next = { ...draft.special };
    for (const key of this.specialKeys) {
      if (values[key] === undefined) continue;
      const value = numberOr(values[key], NaN);
      const minimum = this.getSpecialMinimum(draft, key);
      const maximum = Math.min(
        SPECIAL_MAX,
        numberOr(draft.special_max[key], SPECIAL_MAX),
      );
      if (!Number.isFinite(value) || value < minimum || value > maximum)
        return false;
      next[key] = value;
    }
    const availableAfterChange =
      5 +
      numberOr(draft.bonus_special_points, 0) -
      Object.keys(next).reduce(
        (sum, key) => sum + (numberOr(next[key], 5) - 5),
        0,
      );
    if (availableAfterChange < 0) return false;
    draft.special = next;
    this.validateSpecial(draft);
    this.recalculate(draft);
    return true;
  }

  setMainSkill(draft, skillName, value) {
    const skill = draft.skills[skillName];
    if (!skill || !this.canSetMainSkill(draft, skillName, Boolean(value)))
      return false;
    if (value) {
      const maximum = this.effectiveSkillMax(draft, skillName);
      skill.invested = Math.max(
        0,
        skill.invested - Math.max(0, 2 + skill.invested - maximum),
      );
      skill.is_main = true;
      skill.base = 2;
    } else {
      skill.is_main = false;
      skill.base = 0;
    }
    this.recalculate(draft);
    return true;
  }

  changeSkillRank(draft, skillName, delta) {
    const skill = draft.skills[skillName];
    const amount = numberOr(delta, 0);
    if (!skill || ![-1, 1].includes(amount)) return false;
    if (
      amount > 0 &&
      this.availableSkillRanks(draft) > 0 &&
      this.totalSkillRank(skill) < this.effectiveSkillMax(draft, skillName)
    ) {
      skill.invested += 1;
      this.recalculate(draft);
      return true;
    }
    if (amount < 0 && skill.invested > 0) {
      skill.invested -= 1;
      this.recalculate(draft);
      return true;
    }
    return false;
  }

  addPerk(draft, perkName) {
    const current = numberOr(draft.perks[perkName], 0);
    return this.setPerkRank(draft, perkName, current + 1);
  }

  setPerkRank(draft, perkName, targetRank) {
    const perk = this.catalog.perks.find((item) => item.name === perkName);
    if (!perk) return false;

    const current = numberOr(draft.perks[perkName], 0);
    const maximum = numberOr(perk.ranks, 1);
    const target = numberOr(targetRank, 0);
    if (
      !Number.isInteger(target) ||
      target < 0 ||
      target > maximum ||
      target === current
    ) {
      return false;
    }

    if (target > current) {
      if (!this.meetsRequirements(draft, perk.requirements || {})) return false;
      if (target - current > this.freePerkSlots(draft)) return false;
    }

    if (target === 0) {
      delete draft.perks[perkName];
    } else {
      draft.perks[perkName] = target;
    }
    this.recalculate(draft);
    return true;
  }

  removePerk(draft, perkName) {
    const current = numberOr(draft.perks[perkName], 0);
    return current ? this.setPerkRank(draft, perkName, current - 1) : false;
  }

  applyPendingChoice(draft, selection, choiceIndex = 0) {
    const choices = draft.pending_choices || [];
    const choice = choices.filter((item) =>
      ["special_bonus", "main_skill_from_list", "main_skill_any"].includes(
        item.type,
      ),
    )[choiceIndex];
    if (
      !choice ||
      !Array.isArray(selection) ||
      selection.length !== numberOr(choice.count, 1)
    )
      return false;
    if (new Set(selection).size !== selection.length) return false;

    if (choice.type === "special_bonus") {
      if (!selection.every((key) => this.specialKeys.has(key))) return false;
      for (const key of selection) {
        draft.special[key] += numberOr(choice.value, 1);
        if (!draft.protected_special_attrs.includes(key))
          draft.protected_special_attrs.push(key);
      }
      draft.bonus_special_applied = true;
    } else {
      const allowed =
        choice.type === "main_skill_from_list"
          ? choice.options || []
          : [...this.skillNames];
      if (
        !selection.every(
          (name) => allowed.includes(name) && this.skillNames.has(name),
        )
      )
        return false;
      for (const name of selection) {
        draft.skills[name].is_main = true;
        draft.skills[name].base = 2;
        if (!draft.locked_main_skills.includes(name))
          draft.locked_main_skills.push(name);
      }
    }
    const index = draft.pending_choices.indexOf(choice);
    if (index >= 0) draft.pending_choices.splice(index, 1);
    this.validateSpecial(draft);
    this.validateSkills(draft);
    this.recalculate(draft);
    return true;
  }

  getSummary(draft) {
    return {
      name: draft.name,
      gender: draft.gender,
      backstory: draft.backstory,
      origin: draft.origin,
      origin_traits: [...(draft.origin_traits || [])],
      level: draft.level,
      is_robot: draft.is_robot,
      special: clone(draft.special),
      special_bonus: clone(draft.special_bonus),
      special_max: clone(draft.special_max),
      special_points_available: this.specialPointsAvailable(draft),
      skills: clone(draft.skills),
      skill_ranks_available: this.availableSkillRanks(draft),
      perks: clone(draft.perks),
      perk_slots: {
        total: draft.level + numberOr(draft.bonus_perk_slots, 0),
        used: Object.values(draft.perks || {}).reduce(
          (sum, rank) => sum + numberOr(rank, 0),
          0,
        ),
        free: this.freePerkSlots(draft),
      },
      pending_choices: clone(draft.pending_choices || []),
      luck_points: draft.luck_points,
      luck_points_max: draft.luck_points_max,
      secondary_stats: clone(draft.secondary_stats),
      resistances: clone(draft.resistances),
      equipment: clone(draft.equipment),
    };
  }

  buildExportPayload(draft) {
    return {
      version: 1,
      exported_at: new Date().toISOString(),
      draft: clone(draft),
      summary: this.getSummary(draft),
    };
  }

  findOrigin(name) {
    return this.catalog.origins.find((origin) => origin.name === name);
  }

  findTrait(draft, traitName) {
    return this.findOrigin(draft.origin)?.traits?.find(
      (trait) => trait.name === traitName,
    );
  }

  validTraitSelection(origin, selected) {
    const groups = selected.map(
      (name) =>
        origin.traits.find((trait) => trait.name === name)?.selection_group ||
        "default",
    );
    if (origin.selection_mode === "survivor_ncr") {
      return new Set(groups).size <= 1;
    }
    if (origin.selection_mode === "wastelander") {
      return (
        new Set(groups).size <= 1 ||
        (new Set(groups).size === 2 &&
          groups.every((group) => ["survivor", "ncr"].includes(group)))
      );
    }
    return selected.length <= 1;
  }

  resetTraitState(draft) {
    const fresh = this.createDraft();
    const identity = {
      level: draft.level,
      name: draft.name,
      gender: draft.gender,
      backstory: draft.backstory,
      portrait: draft.portrait,
      portrait_original: draft.portrait_original,
      origin: draft.origin,
      is_robot: draft.is_robot,
    };
    Object.keys(draft).forEach((key) => {
      delete draft[key];
    });
    Object.assign(draft, fresh, identity);
  }

  rebuildTraitState(draft, origin, selected) {
    const preserved = {
      level: draft.level,
      name: draft.name,
      gender: draft.gender,
      backstory: draft.backstory,
      portrait: draft.portrait,
      portrait_original: draft.portrait_original,
      origin: draft.origin,
      origin_traits: [...selected],
      is_robot: draft.is_robot,
    };
    this.resetTraitState(draft);
    Object.assign(draft, preserved);
    selected.forEach((name) => this.applyTrait(draft, origin, name));
    if (["survivor_ncr", "wastelander"].includes(origin.selection_mode)) {
      draft.bonus_perk_slots = selected.length === 1 ? 1 : 0;
    }
    this.validateSkills(draft);
    this.validateSpecial(draft);
    this.recalculate(draft);
  }

  applyTrait(draft, origin, traitName) {
    const trait = origin.traits.find((item) => item.name === traitName);
    if (!trait) return;
    const effects = trait.effects || {};
    const drawbacks = trait.drawbacks || {};
    const skillEffects = effects.skills || {};
    for (const name of skillEffects.set_main || []) {
      if (draft.skills[name]) {
        draft.skills[name].is_main = true;
        draft.skills[name].base = 2;
        if (!draft.locked_main_skills.includes(name))
          draft.locked_main_skills.push(name);
      }
    }
    if (skillEffects.add_main_skill_choice !== undefined) {
      draft.pending_choices.push({
        type: Array.isArray(skillEffects.add_main_skill_choice)
          ? "main_skill_from_list"
          : "main_skill_any",
        options: Array.isArray(skillEffects.add_main_skill_choice)
          ? skillEffects.add_main_skill_choice
          : undefined,
        count: numberOr(skillEffects.choice_count, 1),
        source: `${origin.name}: ${traitName}`,
        is_bonus: true,
      });
    }
    Object.entries(skillEffects.bonus_ranks || {}).forEach(([name, ranks]) => {
      if (draft.skills[name]) draft.skills[name].invested += numberOr(ranks, 0);
    });
    if (skillEffects.max_rank !== undefined)
      draft.skill_max_rank = Math.min(
        draft.skill_max_rank,
        numberOr(skillEffects.max_rank, MAX_SKILL_RANK),
      );

    const specialEffects = effects.special || {};
    Object.entries(specialEffects.bonus || {}).forEach(([key, value]) => {
      if (this.specialKeys.has(key))
        draft.special_bonus[key] += numberOr(value, 0);
    });
    Object.entries(specialEffects.max_increase || {}).forEach(
      ([key, value]) => {
        if (this.specialKeys.has(key))
          draft.special_max[key] = numberOr(value, SPECIAL_MAX);
      },
    );
    Object.entries(specialEffects.max_decrease || {}).forEach(
      ([key, value]) => {
        if (this.specialKeys.has(key))
          draft.special_max[key] = Math.min(
            draft.special_max[key],
            numberOr(value, SPECIAL_MAX),
          );
      },
    );
    if (specialEffects.bonus_choice !== undefined) {
      const count = numberOr(specialEffects.bonus_choice, 0);
      const value = numberOr(specialEffects.bonus_value, 1);
      draft.bonus_special_points = count * value;
      draft.pending_choices.push({
        type: "special_bonus",
        count,
        value,
        source: `${origin.name}: ${traitName}`,
      });
    }
    for (const immunity of effects.immunities || []) {
      if (["radiation", "poison", "disease"].includes(immunity))
        draft.resistances[immunity] = 999;
    }
    Object.entries(effects.resistances || {}).forEach(([type, data]) => {
      if (draft.resistances[type] >= 999) return;
      draft.resistances[type] =
        typeof data === "object"
          ? numberOr(data.base, draft.resistances[type])
          : numberOr(data, draft.resistances[type]);
    });
    if (effects.carry_weight !== undefined)
      draft.carry_weight_formula = String(effects.carry_weight);
    if (effects.carry_weight_formula !== undefined)
      draft.carry_weight_formula = effects.carry_weight_formula;
    draft.bonus_perk_slots += numberOr(effects.bonus_perk_slots, 0);
    draft.combat_modifiers.melee_damage_bonus += numberOr(
      effects.combat?.melee_damage_bonus,
      0,
    );

    if (drawbacks.luck_max_score_reduction !== undefined)
      draft._luck_reduction_from_trait = numberOr(
        drawbacks.luck_max_score_reduction,
        0,
      );
    if (drawbacks.carry_weight_formula !== undefined)
      draft.carry_weight_formula = drawbacks.carry_weight_formula;
    if (drawbacks.skill_max_rank !== undefined)
      draft.skill_max_rank = Math.min(
        draft.skill_max_rank,
        numberOr(drawbacks.skill_max_rank, MAX_SKILL_RANK),
      );
    if (drawbacks.science_cannot_be_main) draft._science_cannot_be_main = true;
    if (drawbacks.affected_skills && drawbacks.skill_max_rank !== undefined) {
      draft._skill_max_rank_restrictions = {
        skills: drawbacks.affected_skills,
        max_rank: numberOr(drawbacks.skill_max_rank, MAX_SKILL_RANK),
      };
    }
  }

  getBonusMainSkills(draft) {
    return draft.locked_main_skills.filter((name) => {
      const setByTrait = (draft.origin_traits || []).some((selected) => {
        const trait = this.findTrait(draft, selected);
        return trait?.effects?.skills?.set_main?.includes(name);
      });
      return !setByTrait;
    });
  }

  canSetMainSkill(draft, skillName, value) {
    const skill = draft.skills[skillName];
    if (!skill) return false;
    if (!value)
      return skill.is_main && !draft.locked_main_skills.includes(skillName);
    const scienceName = this.catalog.skills.find((skillData) =>
      /\u043d\u0430\u0443\u043a/i.test(skillData.name),
    )?.name;
    if (
      skill.is_main ||
      (draft._science_cannot_be_main && skillName === scienceName)
    )
      return false;
    if (
      !this.getBonusMainSkills(draft).includes(skillName) &&
      this.selectedMainSkills(draft) >= MAIN_SKILL_LIMIT
    )
      return false;
    return 2 <= this.effectiveSkillMax(draft, skillName);
  }

  selectedMainSkills(draft) {
    const bonus = new Set(this.getBonusMainSkills(draft));
    return Object.entries(draft.skills).filter(
      ([name, skill]) => skill.is_main && !bonus.has(name),
    ).length;
  }

  totalSkillRank(skill) {
    return numberOr(skill?.base, 0) + numberOr(skill?.invested, 0);
  }

  maxSkillRank(draft, skillName) {
    let maximum = numberOr(draft.skill_max_rank, MAX_SKILL_RANK);
    if (draft._skill_max_rank_restrictions?.skills?.includes(skillName)) {
      maximum = Math.min(
        maximum,
        numberOr(draft._skill_max_rank_restrictions.max_rank, maximum),
      );
    }
    return maximum;
  }

  effectiveSkillMax(draft, skillName) {
    const maximum = this.maxSkillRank(draft, skillName);
    return draft.level < 3 ? Math.min(maximum, CREATION_SKILL_CAP) : maximum;
  }

  availableSkillRanks(draft) {
    return (
      9 +
      numberOr(this.specialValueByLetter(draft, "I"), 5) -
      Object.values(draft.skills).reduce(
        (sum, skill) => sum + numberOr(skill.invested, 0),
        0,
      )
    );
  }

  freePerkSlots(draft) {
    return (
      draft.level +
      numberOr(draft.bonus_perk_slots, 0) -
      Object.values(draft.perks || {}).reduce(
        (sum, rank) => sum + numberOr(rank, 0),
        0,
      )
    );
  }

  meetsRequirements(draft, requirements) {
    return Object.entries(requirements).every(([key, value]) => {
      if (key === "level") return draft.level >= numberOr(value, 0);
      if (key === "robot") return !value || draft.is_robot;
      if (key === "not_robot") return !value || !draft.is_robot;
      return (
        !this.specialKeys.has(key) ||
        numberOr(draft.special[key], 0) >= numberOr(value, 0)
      );
    });
  }

  getSpecialFinalValue(draft, key) {
    return Math.min(
      numberOr(draft.special[key], 5) + numberOr(draft.special_bonus[key], 0),
      numberOr(draft.special_max[key], SPECIAL_MAX),
    );
  }

  getSpecialMinimum(draft, key) {
    return (draft.protected_special_attrs || []).includes(key)
      ? 5
      : SPECIAL_MIN;
  }

  specialPointsAvailable(draft) {
    return (
      5 +
      numberOr(draft.bonus_special_points, 0) -
      Object.keys(draft.special).reduce(
        (sum, key) => sum + (numberOr(draft.special[key], 5) - 5),
        0,
      )
    );
  }

  validateSpecial(draft) {
    Object.keys(draft.special).forEach((key) => {
      const min = this.getSpecialMinimum(draft, key);
      const max = Math.min(
        SPECIAL_MAX,
        numberOr(draft.special_max[key], SPECIAL_MAX),
      );
      draft.special[key] = Math.min(
        max,
        Math.max(min, numberOr(draft.special[key], 5)),
      );
    });
  }

  validateSkills(draft) {
    Object.entries(draft.skills).forEach(([name, skill]) => {
      const max = this.effectiveSkillMax(draft, name);
      skill.invested = Math.max(
        0,
        Math.min(
          numberOr(skill.invested, 0),
          Math.max(0, max - numberOr(skill.base, 0)),
        ),
      );
      if (skill.is_main && numberOr(skill.base, 0) < 2) skill.base = 2;
      if (skill.is_main && 2 > max) {
        skill.is_main = false;
        skill.base = 0;
      }
    });
    draft.locked_main_skills.forEach((name) => {
      if (!draft.skills[name]) return;
      draft.skills[name].is_main = true;
      draft.skills[name].base = 2;
    });
  }

  recalculate(draft) {
    this.recalculateEquipment(draft);
    const special = Object.fromEntries(
      Object.keys(draft.special).map((key) => [
        key,
        this.getSpecialFinalValue(draft, key),
      ]),
    );
    const strength = this.specialValueByLetter(draft, "S", special);
    let carryWeight;
    if (draft.carry_weight_formula === "150 + (5 * strength)")
      carryWeight = 150 + strength * 5;
    else if (/^\d+$/.test(String(draft.carry_weight_formula || "")))
      carryWeight = Number(draft.carry_weight_formula);
    else carryWeight = draft.is_robot ? 150 : 150 + strength * 10;
    const meleeBonus =
      strength <= 6 ? 0 : strength <= 8 ? 1 : strength <= 10 ? 2 : 3;
    draft.luck_points_max = Math.max(
      1,
      this.specialValueByLetter(draft, "L", special) -
        numberOr(draft._luck_reduction_from_trait, 0),
    );
    draft.luck_points = Math.min(
      numberOr(draft.luck_points, draft.luck_points_max),
      draft.luck_points_max,
    );
    draft.secondary_stats = {
      carry_weight: carryWeight,
      physical_dr: numberOr(draft.resistances.physical, 0),
      energy_dr: numberOr(draft.resistances.energy, 0),
      radiation_dr: numberOr(draft.resistances.radiation, 0),
      toxic_dr: numberOr(draft.resistances.poison, 0),
      disease_dr: numberOr(draft.resistances.disease, 0),
      defense: this.specialValueByLetter(draft, "L", special) >= 9 ? 2 : 1,
      initiative:
        this.specialValueByLetter(draft, "P", special) +
        this.specialValueByLetter(draft, "L", special),
      max_hp:
        this.specialValueByLetter(draft, "E", special) +
        this.specialValueByLetter(draft, "L", special) +
        (draft.level - 1),
      melee_bonus:
        meleeBonus + numberOr(draft.combat_modifiers?.melee_damage_bonus, 0),
    };
    draft.summary = this.getSummary(draft);
    return draft;
  }

  recalculateEquipment(draft) {
    const selected = new Map();
    const addSource = (item, skillName, itemId = item.id) => {
      const current = selected.get(itemId) || {
        ...clone(item),
        source_skills: [],
      };
      if (!current.source_skills.includes(skillName))
        current.source_skills.push(skillName);
      selected.set(itemId, current);
    };
    this.catalog.skills.forEach((skill) => {
      if (!draft.skills?.[skill.name]?.is_main) return;
      (this.catalog.equipment_sets[skill.equipment_set] || []).forEach(
        (group, groupIndex) => {
          const items = (group.items || [])
            .map((itemId) => this.equipmentById.get(itemId))
            .filter(Boolean);
          if (group.choice) {
            if (!items.length) return;
            const choiceId = `choice:${skill.id || skill.name}:${groupIndex}`;
            addSource(
              {
                id: choiceId,
                name: items.map((item) => item.name).join(" или "),
                description: "Выберите один вариант",
                options: clone(items),
              },
              skill.name,
              choiceId,
            );
            return;
          }
          items.forEach((item) => addSource(item, skill.name));
        },
      );
    });
    draft.equipment = [...selected.values()];
  }

  specialValueByLetter(draft, letter, values = draft.special) {
    const attribute = this.catalog.special.find(
      (item) => item.letter === letter,
    );
    return numberOr(values[attribute?.key], 5);
  }
}

export const characterRuleConstants = {
  SPECIAL_MIN,
  SPECIAL_MAX,
  MAX_LEVEL,
  MAX_SKILL_RANK,
  MAIN_SKILL_LIMIT,
};
export type { CharacterCatalog, CharacterDraft };
